// @tier: PREMIUM
// Controller du composeur d'annonce admin → parents ou chauffeurs (/notifications/new).
// Conception : docs/ideas/admin-broadcast-composer.md et admin-broadcast-drivers.md
import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';
import { FORM_LIMITS, PAGINATION } from '../config/constants';
import { DEFAULT_PRIORITY } from '../utils/notificationPriority';

export const RECIPIENT_TYPES = { PARENT: 'parent', DRIVER: 'driver' };
export const AUDIENCE_MODES = { SCHOOL: 'school', ROUTES: 'routes', STOPS: 'stops', CUSTOM: 'custom' };
// Miroir de MODES_BY_RECIPIENT du backend : le ciblage par arrêt n'existe pas pour les chauffeurs.
export const MODES_BY_RECIPIENT = {
  [RECIPIENT_TYPES.PARENT]: [AUDIENCE_MODES.SCHOOL, AUDIENCE_MODES.ROUTES, AUDIENCE_MODES.STOPS, AUDIENCE_MODES.CUSTOM],
  [RECIPIENT_TYPES.DRIVER]: [AUDIENCE_MODES.SCHOOL, AUDIENCE_MODES.ROUTES, AUDIENCE_MODES.CUSTOM],
};
export const SENT_PAGE_PATH = '/admin/notifications/sent';
export const NOTIFICATIONS_PATH = '/admin/notifications';

const URGENT = 'urgent';

const ERROR_MESSAGES = {
  AUDIENCE_EMPTY: 'Aucun destinataire ne correspond à cette audience.',
  AUDIENCE_FOREIGN_IDS: 'Certains éléments sélectionnés ne font pas partie de votre école.',
  PREMIUM_REQUIRED: "Cette fonctionnalité n'est pas disponible avec votre offre actuelle.",
};

/** « parent(s) » / « chauffeur(s) » selon le type et la quantité. */
export const recipientNoun = (recipientType, count = 2) => {
  const noun = recipientType === RECIPIENT_TYPES.DRIVER ? 'chauffeur' : 'parent';
  return count > 1 ? `${noun}s` : noun;
};

/** Clé d'idempotence : générée à l'ouverture de la page, conservée si l'admin réessaie après une erreur réseau. */
export const newIdempotencyKey = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

/** Audience envoyée à l'API : `ids` seulement hors « toute l'école ». */
export const buildAudience = (mode, ids) => (mode === AUDIENCE_MODES.SCHOOL ? { mode } : { mode, ids });

/** Langues renseignées (titre ET message), le français toujours en premier. */
export const filledLanguages = (form) => {
  const languages = ['fr'];
  if (form.titleEn.trim() && form.messageEn.trim()) languages.push('en');
  if (form.titleAr.trim() && form.messageAr.trim()) languages.push('ar');
  return languages;
};

/** Erreurs de saisie par champ ; `valid` est vrai quand il n'y en a aucune. */
export const validateBroadcastForm = (form) => {
  const errors = {};
  if (!form.title.trim()) errors.title = 'Le titre est obligatoire';
  else if (form.title.length > FORM_LIMITS.BROADCAST_TITLE_MAX) errors.title = 'Titre trop long';
  if (!form.message.trim()) errors.message = 'Le message est obligatoire';
  else if (form.message.length > FORM_LIMITS.BROADCAST_BODY_MAX) errors.message = 'Message trop long';

  for (const [suffix, label] of [
    ['En', 'anglaise'],
    ['Ar', 'arabe'],
  ]) {
    const title = form[`title${suffix}`];
    const message = form[`message${suffix}`];
    if (Boolean(title.trim()) !== Boolean(message.trim())) {
      errors[`translation${suffix}`] = `Version ${label} incomplète : titre et message requis`;
    }
    if (title.length > FORM_LIMITS.BROADCAST_TITLE_MAX) errors[`title${suffix}`] = 'Titre trop long';
    if (message.length > FORM_LIMITS.BROADCAST_BODY_MAX) errors[`message${suffix}`] = 'Message trop long';
  }
  return { errors, valid: Object.keys(errors).length === 0 };
};

/** Payload de POST /broadcasts : seules les langues renseignées partent. */
export const buildBroadcastPayload = ({
  recipientType = RECIPIENT_TYPES.PARENT,
  audienceMode,
  selectedIds,
  form,
  idempotencyKey,
}) => {
  const payload = {
    recipient_type: recipientType,
    audience: buildAudience(audienceMode, selectedIds),
    title: form.title.trim(),
    message: form.message.trim(),
    priority: form.priority,
    idempotency_key: idempotencyKey,
  };
  const languages = filledLanguages(form);
  if (languages.includes('en')) {
    payload.title_en = form.titleEn.trim();
    payload.message_en = form.messageEn.trim();
  }
  if (languages.includes('ar')) {
    payload.title_ar = form.titleAr.trim();
    payload.message_ar = form.messageAr.trim();
  }
  return payload;
};

/** Confirmation renforcée : toute l'école (ou tous les chauffeurs) ou priorité urgente. */
export const requiresStrongConfirmation = (audienceMode, priority) =>
  audienceMode === AUDIENCE_MODES.SCHOOL || priority === URGENT;

/** Liste où chercher les éléments à cocher, `null` pour « toute l'école ». */
export const optionsSourceFor = (recipientType, audienceMode) => {
  if (audienceMode === AUDIENCE_MODES.ROUTES) return 'routes';
  if (audienceMode === AUDIENCE_MODES.STOPS) return 'stops';
  if (audienceMode === AUDIENCE_MODES.CUSTOM) return recipientType === RECIPIENT_TYPES.DRIVER ? 'drivers' : 'parents';
  return null;
};

/** Nom affiché d'une personne dans une liste à cocher. */
export const personName = (person) =>
  [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || 'Sans nom';

const emptyForm = {
  title: '',
  message: '',
  titleEn: '',
  messageEn: '',
  titleAr: '',
  messageAr: '',
  priority: DEFAULT_PRIORITY,
};

/** Préremplissage venant du bouton « Dupliquer » (location.state.prefill). */
const readPrefill = (state) => {
  const prefill = state?.prefill;
  if (!prefill) return null;
  const recipientType =
    prefill.recipientType === RECIPIENT_TYPES.DRIVER ? RECIPIENT_TYPES.DRIVER : RECIPIENT_TYPES.PARENT;
  const mode = MODES_BY_RECIPIENT[recipientType].includes(prefill.audience?.mode)
    ? prefill.audience.mode
    : AUDIENCE_MODES.SCHOOL;
  return {
    recipientType,
    audienceMode: mode,
    selectedIds: mode === AUDIENCE_MODES.SCHOOL ? [] : prefill.audience?.ids || [],
    form: {
      ...emptyForm,
      title: prefill.title || '',
      message: prefill.message || '',
      titleEn: prefill.titleEn || '',
      messageEn: prefill.messageEn || '',
      titleAr: prefill.titleAr || '',
      messageAr: prefill.messageAr || '',
      priority: prefill.priority || DEFAULT_PRIORITY,
    },
  };
};

export const useBroadcast = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const prefill = useMemo(() => readPrefill(location.state), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [recipientType, setRecipientType] = useState(prefill?.recipientType ?? RECIPIENT_TYPES.PARENT);
  const [audienceMode, setAudienceMode] = useState(prefill?.audienceMode ?? AUDIENCE_MODES.SCHOOL);
  const [selectedIds, setSelectedIds] = useState(prefill?.selectedIds ?? []);
  const [audienceSearch, setAudienceSearch] = useState('');
  const [form, setForm] = useState(prefill?.form ?? emptyForm);
  const [showTranslations, setShowTranslations] = useState(
    Boolean(prefill && (prefill.form.titleEn || prefill.form.titleAr)),
  );
  const [previewLanguage, setPreviewLanguage] = useState('fr');
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());

  const source = optionsSourceFor(recipientType, audienceMode);

  const routesQuery = useQuery({
    queryKey: ['broadcast-audience-routes'],
    enabled: source === 'routes',
    queryFn: async () => {
      const response = await unifiedApi.routes.getAll({ status: 'active', limit: PAGINATION.LOOKUP_LIMIT });
      return (response.data || []).map((route) => ({ id: route.id, name: route.route_name || route.route_code }));
    },
  });

  const stopsQuery = useQuery({
    queryKey: ['broadcast-audience-stops'],
    enabled: source === 'stops',
    queryFn: async () => {
      const response = await unifiedApi.stops.getAllUnlimited({ status: 'active' });
      return (response.data || []).map((stop) => ({ id: stop.id, name: stop.stop_name }));
    },
  });

  const parentsQuery = useQuery({
    queryKey: ['broadcast-audience-parents'],
    enabled: source === 'parents',
    queryFn: async () => {
      const response = await unifiedApi.parents.getAllUnlimited({ status: 'active' });
      return (response.data || [])
        .filter((parent) => parent.status === 'active')
        .map((parent) => ({ id: parent.id, name: personName(parent) }));
    },
  });

  const driversQuery = useQuery({
    queryKey: ['broadcast-audience-drivers'],
    enabled: source === 'drivers',
    queryFn: async () => {
      const response = await unifiedApi.drivers.getAll({ status: 'active', limit: PAGINATION.LOOKUP_LIMIT });
      // Seuls les chauffeurs actifs reçoivent une annonce (en congé et inactifs sont exclus côté API).
      return (response.data || [])
        .filter((driver) => driver.status === 'active')
        .map((driver) => ({ id: driver.id, name: personName(driver) }));
    },
  });

  const activeQuery = { routes: routesQuery, stops: stopsQuery, parents: parentsQuery, drivers: driversQuery }[source];
  const allOptions = activeQuery?.data;
  const audienceLoading = Boolean(activeQuery?.isLoading);

  const audienceOptions = useMemo(() => {
    const options = allOptions || [];
    const term = audienceSearch.trim().toLowerCase();
    return term ? options.filter((option) => (option.name || '').toLowerCase().includes(term)) : options;
  }, [allOptions, audienceSearch]);

  // Un élément supprimé depuis la copie d'une annonce est ignoré.
  useEffect(() => {
    if (!allOptions) return;
    const known = new Set(allOptions.map((option) => option.id));
    setSelectedIds((current) =>
      current.every((id) => known.has(id)) ? current : current.filter((id) => known.has(id)),
    );
  }, [allOptions]);

  const audienceReady = audienceMode === AUDIENCE_MODES.SCHOOL || selectedIds.length > 0;
  const previewQuery = useQuery({
    queryKey: ['broadcast-preview', recipientType, audienceMode, [...selectedIds].sort().join(',')],
    enabled: audienceReady,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await unifiedApi.notifications.previewBroadcast(buildAudience(audienceMode, selectedIds), recipientType)).data,
  });
  const preview = audienceReady ? previewQuery.data : undefined;

  const validation = useMemo(() => validateBroadcastForm(form), [form]);
  const languages = useMemo(() => filledLanguages(form), [form]);
  const strongConfirmation = requiresStrongConfirmation(audienceMode, form.priority);
  const previewingNow = audienceReady && (previewQuery.isPending || previewQuery.isPlaceholderData);
  const noRecipient = audienceReady && !previewingNow && preview?.total === 0;
  const canSubmit = validation.valid && audienceReady && !previewingNow && !noRecipient && !sending;

  // La langue d'aperçu retombe sur le français quand sa traduction est vidée.
  const activePreviewLanguage = languages.includes(previewLanguage) ? previewLanguage : 'fr';

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const resetAudience = (mode) => {
    setAudienceMode(mode);
    setSelectedIds([]);
    setAudienceSearch('');
  };

  const changeAudienceMode = (mode) => resetAudience(mode);

  // Changer de destinataire réinitialise l'audience mais conserve le titre et le message saisis.
  const changeRecipientType = (type) => {
    if (type === recipientType) return;
    setRecipientType(type);
    resetAudience(AUDIENCE_MODES.SCHOOL);
  };

  const toggleAudienceId = (id) =>
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  // « Tout sélectionner » agit sur les résultats visibles (filtrés par la recherche).
  const selectAllVisible = () =>
    setSelectedIds((current) => [...new Set([...current, ...audienceOptions.map((option) => option.id)])]);
  const clearSelection = () => setSelectedIds([]);

  const requestSend = () => {
    setShowErrors(true);
    if (!canSubmit) return;
    setConfirmChecked(false);
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    if (strongConfirmation && !confirmChecked) return;
    setSending(true);
    try {
      const payload = buildBroadcastPayload({
        recipientType,
        audienceMode,
        selectedIds,
        form,
        idempotencyKey: idempotencyKey.current,
      });
      const response = await unifiedApi.notifications.sendBroadcast(payload);
      const { id, replayed } = response.data;
      // Une réponse « déjà traitée » (clé rejouée) vaut succès.
      toast.success(replayed ? 'Annonce déjà envoyée' : 'Annonce envoyée');
      idempotencyKey.current = newIdempotencyKey();
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
      setConfirmOpen(false);
      navigate(`${SENT_PAGE_PATH}?id=${id}`);
    } catch (error) {
      logger.error("Erreur lors de l'envoi de l'annonce:", error);
      const code = error.response?.data?.error;
      toast.error(ERROR_MESSAGES[code] || error.message || "Erreur lors de l'envoi de l'annonce");
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  return {
    recipientType,
    changeRecipientType,
    availableModes: MODES_BY_RECIPIENT[recipientType],
    audienceMode,
    changeAudienceMode,
    selectedIds,
    toggleAudienceId,
    selectAllVisible,
    clearSelection,
    audienceSearch,
    setAudienceSearch,
    audienceOptions,
    audienceLoading,
    form,
    updateField,
    showTranslations,
    setShowTranslations,
    languages,
    previewLanguage: activePreviewLanguage,
    setPreviewLanguage,
    errors: showErrors ? validation.errors : {},
    preview,
    previewingNow,
    previewFailed: audienceReady && previewQuery.isError,
    noRecipient,
    canSubmit,
    strongConfirmation,
    confirmOpen,
    setConfirmOpen,
    confirmChecked,
    setConfirmChecked,
    sending,
    requestSend,
    confirmSend,
    cancel: () => navigate(NOTIFICATIONS_PATH),
  };
};

export default useBroadcast;
